import { Flex, Input, InputGroup } from '@chakra-ui/react';
import { ChangeEvent, memo, useCallback, useEffect, useMemo } from 'react';

import { AutoCompleteInputGroupProps, AutoCompleteInputProps } from './AutoCompleteInput.types';
import { AutoCompleteInputProvider, useAutoCompleteContext } from './AutoCompleteInputContext';
import { AutoCompleteInputList } from './AutoCompleteInputList';
import { AutoCompleteInputTag } from './AutoCompleteInputTag';

export const AUTO_COMPLETE_SEARCH_INPUT_ID = 'auto-complete-search-input';
const AutoCompleteInputMain = memo(
  ({
    children,
    multiple = false,
    placeholder,
    defaultValue,
    label,
    onChange,
    onIndividualInputChange,
    focusOnMenuOpen,
  }: AutoCompleteInputProps) => {
    const {
      inputRef,
      value,
      setValue,
      setConfig,
      selectedIds,
      isListVisible,
      onKeyDown,
      onInputBlur,
      onInputFocus,
      activeDescendentId,
    } = useAutoCompleteContext();

    // Find AutoCompleteInputGroup child - will be rendered inside InputGroup
    const inputGroupChildren = useMemo(() => {
      if (!Array.isArray(children)) return null;
      const child = children.find((child) => child.type.displayName === AutoCompleteInputGroup.displayName);
      return child ? child.props.children : null;
    }, [children]);

    // Find AutoCompleteInputList child - will be rendered after InputGroup
    const listChildren = useMemo(() => {
      if (Array.isArray(children)) {
        return children.find((child) => child.type.displayName === AutoCompleteInputList.displayName);
      } else {
        return children;
      }
    }, [children]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onIndividualInputChange?.(event.target.value);
        setValue(event.target.value);
      },
      [onIndividualInputChange, setValue],
    );

    useEffect(() => {
      if (onChange) onChange(selectedIds);
    }, [selectedIds, multiple, onChange]);

    // Reset config when props changed
    useEffect(() => {
      setConfig({ isMultiple: multiple });
    }, [multiple, setConfig]);

    // Set initial value if defined on mount
    useEffect(() => {
      if (defaultValue) {
        setValue(defaultValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (focusOnMenuOpen) {
        inputRef?.current?.focus();
      }
    }, [focusOnMenuOpen, inputRef]);

    const autoCompleteInputTestId = 'auto-complete-input';

    return (
      <Flex direction={'column'} width='full' height='full' onKeyDown={onKeyDown}>
        <InputGroup flexDirection='column' variant='input' borderRadius='base' height={10}>
          {multiple && <AutoCompleteInputTag />}
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            data-testid={autoCompleteInputTestId}
            role='combobox'
            aria-autocomplete='list'
            aria-haspopup='listbox'
            aria-expanded={isListVisible}
            aria-label={label}
            aria-activedescendant={activeDescendentId}
            id={AUTO_COMPLETE_SEARCH_INPUT_ID}
            outline='1px solid'
            _focus={{ outline: '2px solid', outlineColor: 'upgrade.blue.500' }}
          />
          {inputGroupChildren}
        </InputGroup>
        {listChildren}
      </Flex>
    );
  },
);

AutoCompleteInputMain.displayName = 'AutoCompleteInputMain';

/**
 * AutoCompleteInputGroup is a wrapper component for children to be included in the InputGroup containing the
 * text input field
 */
export const AutoCompleteInputGroup = memo(({ children }: AutoCompleteInputGroupProps) => <>{children}</>);

AutoCompleteInputGroup.displayName = 'AutoCompleteInputGroup';

export const AutoCompleteInput = memo(
  ({
    children,
    multiple = false,
    onChange,
    submitValueOnEnter,
    placeholder,
    openListOnFocus,
    defaultValue,
    onIndividualInputChange,
    focusOnMenuOpen = false,
  }: AutoCompleteInputProps) => {
    const isValidChildren = useMemo(() => {
      if (Array.isArray(children)) {
        return children.every((child) =>
          [AutoCompleteInputList.displayName, AutoCompleteInputGroup.displayName].includes(child.type.displayName),
        );
      } else {
        // If one child, should be list component
        return children.type.displayName === AutoCompleteInputList.displayName;
      }
    }, [children]);

    if (!isValidChildren) return null;

    return (
      <AutoCompleteInputProvider submitValueOnEnter={submitValueOnEnter} openListOnFocus={openListOnFocus}>
        <AutoCompleteInputMain
          multiple={multiple}
          onChange={onChange}
          placeholder={placeholder}
          defaultValue={defaultValue}
          onIndividualInputChange={onIndividualInputChange}
          focusOnMenuOpen={focusOnMenuOpen}
        >
          {children}
        </AutoCompleteInputMain>
      </AutoCompleteInputProvider>
    );
  },
);

AutoCompleteInput.displayName = 'AutoCompleteInput';
